import { useEffect, useRef, useState } from 'react';

import { Coords } from 'editor/src/store/design/types';

import useFabricCanvas from 'editor/src/util/useFabricCanvas';
import useFabricUtils from 'editor/src/util/useFabricUtils';

import { CanvasRotation } from '../types';

import useRulerGuides from './context';
import { GuideAxis, guidePosition, RulerGuide } from './geometry';

export default function useGuideDrag(coords: Coords, rotation: CanvasRotation) {
  const canvas = useFabricCanvas();
  const { px2mm } = useFabricUtils();
  const { guides, setGuides } = useRulerGuides();
  const [draft, setDraft] = useState<RulerGuide>();
  const capture = useRef<{ element: Element; pointerId: number; clientX: number; clientY: number; offset: number }>();

  function cancel() {
    const active = capture.current;
    if (active?.element.hasPointerCapture(active.pointerId)) {
      active.element.releasePointerCapture(active.pointerId);
    }
    capture.current = undefined;
    setDraft(undefined);
  }

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        cancel();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('blur', cancel);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('blur', cancel);
      const active = capture.current;
      if (active?.element.hasPointerCapture(active.pointerId)) {
        active.element.releasePointerCapture(active.pointerId);
      }
    };
  }, []);

  function position(event: React.PointerEvent, axis: GuideAxis) {
    return guidePosition(axis, canvas.getPointer(event.nativeEvent), coords, px2mm, rotation);
  }

  function startDrag(event: React.PointerEvent, axis: GuideAxis, guide?: RulerGuide) {
    if (event.button !== 0 || !event.isPrimary) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const pointerPosition = position(event, axis);
    capture.current = {
      element: event.currentTarget,
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      offset: guide ? pointerPosition - guide.position : 0,
    };
    setDraft({
      id: guide?.id ?? Math.max(0, ...guides.map((item) => item.id)) + 1,
      axis,
      position: guide?.position ?? pointerPosition,
    });
  }

  function onPointerMove(event: React.PointerEvent) {
    if (draft && event.pointerId === capture.current?.pointerId) {
      event.stopPropagation();
      setDraft({ ...draft, position: position(event, draft.axis) - capture.current.offset });
    }
  }

  function onPointerUp(event: React.PointerEvent) {
    const active = capture.current;
    if (!draft || !active || event.pointerId !== active.pointerId) {
      return;
    }
    event.stopPropagation();
    if (Math.hypot(event.clientX - active.clientX, event.clientY - active.clientY) < 3) {
      cancel();
      return;
    }
    const next = { ...draft, position: position(event, draft.axis) - active.offset };
    if (Number.isFinite(next.position)) {
      const remaining = guides.filter((guide) => guide.id !== next.id);
      setGuides([...remaining, next]);
    }
    cancel();
  }

  return { draft, startDrag, onPointerMove, onPointerUp, onPointerCancel: cancel };
}
